/**
 * Screen router and the persistent 3D stage.
 *
 * The canvas is mounted once for the whole session — the tutorial and every
 * match share it — so switching screens never rebuilds the arena or drops the
 * WebGL context.
 */
import { useEffect } from 'react';
import { GameCanvas } from './scene/GameCanvas';
import { TutorialScreen } from './tutorial/TutorialScreen';
import { Lobby } from './ui/Lobby';
import { MatchScreen } from './gameplay/MatchScreen';
import { PracticeScreen } from './practice/PracticeScreen';
import { useFlow } from './gameplay/flowStore';
import { useNet } from './networking/netStore';
import { useSettings } from './gameplay/settingsStore';
import { installTestHooks } from './testhooks';
import { sfx } from './audio/sfx';
import { mic } from './voice/mic';

export function App() {
  const screen = useFlow((s) => s.screen);
  const hydrate = useFlow((s) => s.hydrate);
  const setScreen = useFlow((s) => s.setScreen);
  const showDebug = useSettings((s) => s.showDebug);
  const setShowDebug = useSettings((s) => s.setShowDebug);

  useEffect(() => {
    hydrate();
    installTestHooks();
  }, [hydrate]);

  /* Debug toggle. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.code === 'Backquote') {
        event.preventDefault();
        setShowDebug(!useSettings.getState().showDebug);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setShowDebug]);

  /* Background tabs stop rendering, animating audio and analysing the mic. */
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        sfx.suspend();
        mic.suspend();
      } else {
        sfx.resume();
        mic.resume();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const inMatch = screen === 'match';
  const inPractice = screen === 'practice';
  // Both room-backed screens need the canvas to accept the clicks that acquire
  // the pointer lock.
  const interactive = inMatch || inPractice;

  return (
    <div className="app">
      <div className="stage">
        <GameCanvas interactive={interactive} />
      </div>

      {screen === 'boot' ? (
        <div className="screen">
          <div className="panel" style={{ textAlign: 'center' }}>
            <div className="title">Spell</div>
            <div className="hint">불러오는 중…</div>
          </div>
        </div>
      ) : null}

      {screen === 'tutorial' ? <TutorialScreen /> : null}

      {screen === 'lobby' ? (
        <Lobby
          onConnected={() => {
            useNet.getState().set({ status: 'connected' });
            setScreen('match');
          }}
          onPractice={() => {
            useNet.getState().set({ status: 'connected' });
            setScreen('practice');
          }}
        />
      ) : null}

      {inMatch ? <MatchScreen onLeave={() => setScreen('lobby')} /> : null}
      {inPractice ? <PracticeScreen onLeave={() => setScreen('lobby')} /> : null}

      {showDebug && !interactive ? (
        <div className="hint" style={{ position: 'absolute', bottom: 8, left: 12 }}>
          debug on — ` 로 끄기
        </div>
      ) : null}
    </div>
  );
}
